import { useId, type ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * **FI-50 + FI-76 follow-up (2026-05-22)** — molecule SSOT per i campi
 * narrativi richiesti al giocatore durante la creazione PG. Sostituisce 3
 * pattern divergenti pre-esistenti:
 *  - shadcn `Input` + `Label` in `MoveNarrativeFieldsField` (nome divinità,
 *    descrizione arma, segno rivelatore, ecc.)
 *  - `<input>` custom border-bottom in `SectionRace` (nome razza custom)
 *  - `<textarea>` custom border-bottom in `SectionEquipment` (Ricordo,
 *    Simbolo sacro, Strumento Musicale, ecc.)
 *
 * **Iterazione 2 (review Francesco 2026-05-22)**: il pattern `.field` del
 * mockup (border-bottom only su bg trasparente) non rendeva evidente
 * l'area di input — lo sfondo era identico al canvas e l'unico indizio
 * era il placeholder. Iterazione: control con **bordo completo** + **bg
 * `paper-bright`** + **rounded-sm**. Lo styling è inline (no più
 * wrapping su `<Field>`) per differenziarlo dal pattern legacy `.field`
 * (usato ancora da `<Field>` per Nome PG, Titolo, Bond text, ecc.).
 *
 * Stati visivi del control:
 *  - **default**: bordo `border-ink-soft` + bg `paper-bright`
 *  - **focus**: bordo `border-ink` (scuro)
 *  - **required+empty** (`aria-invalid=true`): bordo `accent-dwr` (red)
 *
 * Label header uppercase Cinzel sopra (asterisco rosso se `required`),
 * hint italic sotto.
 */
export interface NarrativeTextFieldProps {
  /** ID per il binding label↔input. Auto-generato se omesso. */
  id?: string;
  /** Etichetta breve (resa uppercase Cinzel dallo styling label).
   *  Esempi: "Nome della divinità", "Ricordo della tua terra", "Nome della razza". */
  label: string;
  /** Prompt/descrizione narrativa italic sotto il campo. Opzionale. */
  hint?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  /** Se `true`, mostra asterisco rosso accanto al label e bordo
   *  `accent-dwr` quando vuoto. Default `false`. */
  required?: boolean;
  placeholder?: string;
  /** Textarea multi-riga vs input single-line. Default `false`. */
  multiline?: boolean;
  rows?: number;
  /** Se definito, renderizza un Select shadcn invece di input/textarea. */
  oneOf?: readonly string[];
  /** Helper per il rendering label delle option di `oneOf`. */
  formatOneOfLabel?: (value: string) => string;
}

/**
 * Stile shared del control (input/textarea/Select trigger). Bordo completo
 * sottile + sfondo `paper-bright` per delimitare visivamente l'area di
 * input. Stati `focus` (border-ink) e `aria-invalid=true` (border-accent-dwr).
 */
const CONTROL_BASE_CLASS =
  'border-ink-soft bg-paper-soft text-ink placeholder:text-ink-faint font-serif w-full rounded-sm border px-2.5 py-1.5 text-[0.95rem] outline-none transition-colors focus:border-ink focus:bg-paper-bright';
const CONTROL_INVALID_CLASS = 'border-accent-dwr';
const CONTROL_TEXTAREA_EXTRA_CLASS = 'min-h-[60px] resize-y';
const LABEL_CLASS = 'font-display text-ink-soft text-[0.75rem] tracking-[0.1em] uppercase';
const HINT_CLASS = 'text-ink-faint text-xs italic';

export function NarrativeTextField({
  id: idProp,
  label,
  hint,
  value,
  onChange,
  required = false,
  placeholder,
  multiline = false,
  rows = 3,
  oneOf,
  formatOneOfLabel,
}: NarrativeTextFieldProps) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const isEmpty = value.trim().length === 0;
  const showInvalid = required && isEmpty;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className={LABEL_CLASS}>
        {label}
        {required ? (
          <>
            <sup className="text-accent-dwr ml-0.5" aria-hidden>
              *
            </sup>
            <span className="sr-only"> (obbligatorio)</span>
          </>
        ) : null}
      </label>
      {renderControl({
        inputId,
        value,
        onChange,
        placeholder,
        multiline,
        rows,
        oneOf,
        formatOneOfLabel,
        showInvalid,
      })}
      {hint !== undefined ? <p className={HINT_CLASS}>{hint}</p> : null}
    </div>
  );
}

interface RenderControlArgs {
  inputId: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string | undefined;
  multiline: boolean;
  rows: number;
  oneOf: readonly string[] | undefined;
  formatOneOfLabel: ((value: string) => string) | undefined;
  showInvalid: boolean;
}

function renderControl({
  inputId,
  value,
  onChange,
  placeholder,
  multiline,
  rows,
  oneOf,
  formatOneOfLabel,
  showInvalid,
}: RenderControlArgs): ReactNode {
  if (oneOf && oneOf.length > 0) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={inputId}
          aria-invalid={showInvalid}
          className={cn(
            CONTROL_BASE_CLASS,
            'shadow-none focus:ring-0',
            showInvalid && CONTROL_INVALID_CLASS,
          )}
        >
          <SelectValue placeholder={placeholder ?? 'Seleziona…'} />
        </SelectTrigger>
        <SelectContent>
          {oneOf.map((option) => (
            <SelectItem key={option} value={option}>
              {formatOneOfLabel ? formatOneOfLabel(option) : option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (multiline) {
    return (
      <textarea
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        aria-invalid={showInvalid}
        className={cn(
          CONTROL_BASE_CLASS,
          CONTROL_TEXTAREA_EXTRA_CLASS,
          showInvalid && CONTROL_INVALID_CLASS,
        )}
      />
    );
  }
  return (
    <input
      id={inputId}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-invalid={showInvalid}
      className={cn(CONTROL_BASE_CLASS, showInvalid && CONTROL_INVALID_CLASS)}
    />
  );
}
